import React from 'react';
import { Box, Card, CardContent, Typography, Grid, CircularProgress } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import API from '../../services/api';

const fetchClientsSummary = async () => {
  const response = await API.get('/clients/summary/', {
    params: { ts: new Date().getTime() },
  });
  return response.data;
};

const STATS_CONFIG = [
  {
    key: 'total_clients',
    icon: <PeopleIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 28 } }} />,
    label: 'סה"כ לקוחות',
    color: '#1976d2',
    bgColor: '#e3f2fd',
  },
  {
    key: 'new_clients_this_month',
    icon: <PersonAddIcon sx={{ color: '#2e7d32', fontSize: { xs: 20, sm: 28 } }} />,
    label: 'חדשים החודש',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
  },
  {
    key: 'clients_with_debt',
    icon: <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: { xs: 20, sm: 28 } }} />,
    label: 'לקוחות עם חוב',
    color: '#ed6c02',
    bgColor: '#fff3e0',
  },
];

const StatCard = ({ icon, label, value, color, bgColor, isLoading }) => (
  <Card
    dir="rtl"
    sx={{
      borderRadius: 2,
      boxShadow: 2,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      borderLeft: `4px solid ${color}`,
    }}
  >
    <CardContent sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1.5, sm: 2 },
      width: '100%',
      py: { xs: 1, sm: 1.5 },
      px: { xs: 1.5, sm: 2 },
      '&:last-child': { pb: { xs: 1, sm: 1.5 } },
    }}>
      <Box
        sx={{
          backgroundColor: bgColor,
          borderRadius: '50%',
          width: { xs: 36, sm: 48 },
          height: { xs: 36, sm: 48 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, lineHeight: 1.2 }}>
          {label}
        </Typography>
        {isLoading ? (
          <CircularProgress size={20} sx={{ color, mt: 0.5 }} />
        ) : (
          <Typography sx={{ fontWeight: 'bold', color, lineHeight: 1.2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            {value ?? '-'}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const ClientsSummaryBar = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', 'summary'],
    queryFn: fetchClientsSummary,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  // Log errors to help debug API issues
  if (error) {
    console.error('ClientsSummary API error:', error?.response?.status, error?.response?.data || error?.message);
  }

  return (
    <Box sx={{ mb: { xs: 1, sm: 2 }, px: { xs: 0.5, sm: 0 } }}>
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          alignItems: 'stretch',
          width: '100%',
        }}
      >
        {STATS_CONFIG.map((stat, index) => (
          <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
            <StatCard
              icon={stat.icon}
              label={stat.label}
              value={data?.[stat.key]}
              color={stat.color}
              bgColor={stat.bgColor}
              isLoading={isLoading && !data}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ClientsSummaryBar;
